/* eslint-disable react-hooks/rules-of-hooks */
import { useParams, Link, Navigate } from 'react-router-dom';
import { useMemo } from 'react';
import { Calendar, Clock, User, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockBlogs } from '../data/mock';
import ReactMarkdown from 'react-markdown';

const BlogPost = () => {
  const { id } = useParams();
  const blog = mockBlogs.find(b => b.id === id);

  // If blog not found, redirect to blog list
  if (!blog) {
    return <Navigate to="/blog" replace />;
  }

  // Get related blogs (same category, excluding current)
  const relatedBlogs = mockBlogs
    .filter(b => b.category === blog.category && b.id !== blog.id)
    .slice(0, 3);

  // Memoize markdown components to prevent recreation on every render
  const markdownComponents = useMemo(() => ({
    h1: ({node, ...props}) => <h2 className="text-4xl font-black text-gray-900 mt-12 mb-6 tracking-tight leading-tight" {...props} />,
    h2: ({node, ...props}) => <h3 className="text-3xl font-bold text-gray-900 mt-10 mb-5 tracking-tight leading-tight" {...props} />,
    h3: ({node, ...props}) => <h4 className="text-2xl font-bold text-gray-900 mt-8 mb-4 tracking-tight" {...props} />,
    p: ({node, ...props}) => <p className="text-gray-700 text-lg leading-relaxed mb-6" {...props} />,
    ul: ({node, ...props}) => <ul className="list-disc list-inside text-gray-700 text-lg leading-relaxed mb-6 space-y-2" {...props} />,
    ol: ({node, ...props}) => <ol className="list-decimal list-inside text-gray-700 text-lg leading-relaxed mb-6 space-y-2" {...props} />,
    li: ({node, ...props}) => <li className="ml-4" {...props} />,
    strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
    a: ({node, ...props}) => <a className="text-brand-400 hover:text-brand-500 font-bold underline" {...props} />,
    blockquote: ({node, ...props}) => (
      <blockquote className="border-l-4 border-brand-400 pl-6 py-2 my-8 italic text-gray-700 bg-brand-50/30" {...props} />
    ),
  }), []);

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/blog">
          <Button variant="ghost" className="text-brand-400 hover:text-brand-500 hover:bg-brand-50 font-bold mb-8">
            <ArrowLeft className="mr-2" size={18} />
            Back to Articles
          </Button>
        </Link>
      </div>

      {/* Hero Image */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="aspect-[21/9] overflow-hidden rounded-3xl shadow-2xl">
          <img 
            src={blog.image} 
            alt={blog.title}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="mb-8">
          <span className="text-sm bg-brand-50 text-brand-400 px-4 py-2 rounded-full font-bold">
            {blog.category}
          </span>
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-8 leading-tight tracking-tight">
          {blog.title}
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-12 pb-8 border-b-2 border-gray-200">
          <div className="flex items-center">
            <User size={20} className="mr-2 text-brand-400" />
            <span className="font-bold text-gray-900">{blog.author}</span>
          </div>
          <div className="flex items-center">
            <Calendar size={20} className="mr-2 text-brand-400" />
            <span>{new Date(blog.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
          <div className="flex items-center">
            <Clock size={20} className="mr-2 text-brand-400" />
            <span>{blog.readTime}</span>
          </div>
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <ReactMarkdown components={markdownComponents}>
          >
            {blog.content}
          </ReactMarkdown>
        </div>

        {/* Author Bio */}
        <div className="mt-16 p-8 bg-gradient-to-br from-brand-50 via-white to-brand-50/50 rounded-2xl border-2 border-brand-200">
          <p className="text-sm text-brand-400 font-bold mb-2">ABOUT THE AUTHOR</p>
          <p className="text-gray-700 text-lg leading-relaxed italic">
            {blog.content.split('*')[1]?.trim() || `${blog.author} is a recruitment specialist at Growth Talent Group.`}
          </p>
        </div>

        {/* CTA */}
        <div className="mt-16">
          <Card className="bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 border-0 shadow-xl">
            <CardContent className="p-12 text-center text-white">
              <h3 className="text-3xl md:text-4xl font-black mb-4 tracking-tight leading-tight">
                Ready to Advance Your Career?
              </h3>
              <p className="text-xl mb-8 text-white/95 leading-relaxed">
                Connect with Australia's leading property and strata recruitment specialists
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/jobs">
                  <Button size="lg" className="bg-white text-brand-400 hover:bg-gray-100 font-bold text-lg px-10 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    Browse Jobs
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-brand-400 font-bold text-lg px-10 py-6 bg-transparent backdrop-blur-sm shadow-xl transition-all duration-300 hover:scale-105">
                    Get In Touch
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </article>

      {/* Related Articles */}
      {relatedBlogs.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-black text-gray-900 mb-12 tracking-tight">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedBlogs.map((relatedBlog) => (
                <Link key={relatedBlog.id} to={`/blog/${relatedBlog.id}`}>
                  <Card className="bg-white border-2 border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 group overflow-hidden hover:-translate-y-2 h-full">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={relatedBlog.image} 
                        alt={relatedBlog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      <span className="text-xs bg-brand-50 text-brand-400 px-3 py-1.5 rounded-full font-semibold">
                        {relatedBlog.category}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mt-4 mb-3 group-hover:text-brand-400 transition-colors duration-200 leading-tight line-clamp-2">
                        {relatedBlog.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">{relatedBlog.excerpt}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
